/* Form primitives shared by the client and site forms.
 *
 * These existed as the same handful of Tailwind class strings repeated inline
 * on every input in both pages. Pulling them here keeps focus rings, disabled
 * styling, and label association identical everywhere, and keeps the two forms
 * that now cross-reference each other from drifting apart.
 */

export const inputCls =
  "w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:ring-2 focus:ring-[#16324F] focus:border-[#16324F] outline-none";
export const selectCls =
  "w-full border border-gray-300 rounded-sm px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#16324F] focus:border-[#16324F] outline-none";
export const primaryBtnCls =
  "bg-[#16324F] text-white rounded-sm px-4 py-2 text-sm hover:bg-[#10243a] transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] focus-visible:ring-offset-2";
export const outlineBtnCls =
  "bg-transparent text-[#16324F] border border-gray-300 rounded-sm px-3 py-1.5 text-xs hover:border-[#16324F] hover:bg-[#F1F4F7] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] focus-visible:ring-offset-2";

const Label = ({ htmlFor, children, hint }) => (
  <label htmlFor={htmlFor} className="block text-sm font-medium mb-1.5">
    {children}
    {hint && <span className="font-normal text-ink-muted"> {hint}</span>}
  </label>
);

export const TextField = ({ id, label, hint, testId, mono, className = "mb-3", ...rest }) => (
  <div className={className}>
    <Label htmlFor={id} hint={hint}>{label}</Label>
    <input id={id} data-testid={testId} className={`${inputCls} ${mono ? "font-mono" : ""}`} {...rest} />
  </div>
);

/**
 * Select backed by an option list from lib/taxonomy.js.
 * `placeholder` adds a leading empty option, for fields that may be unset.
 */
export const SelectField = ({ id, label, hint, testId, options, placeholder, describedBy, className = "mb-3", ...rest }) => (
  <div className={className}>
    <Label htmlFor={id} hint={hint}>{label}</Label>
    <select id={id} data-testid={testId} className={selectCls} aria-describedby={describedBy} {...rest}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  </div>
);

/* Explanatory line under a field. Wire it up with the field's describedBy. */
export const FieldHelp = ({ id, children }) => (
  <p id={id} className="text-xs text-ink-muted -mt-2 mb-3">{children}</p>
);
